import React, { PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppendState, BundleMsg } from 'src/components/BundleProduct/feature/bundle.register';
import { AppendState as AppendCartState } from 'src/core/state/cart';
import { DataStatus } from 'src/core/engine/types';
import { MessagesModal } from 'src/components/Modal/MessagesModal';
import { BundleSelectionContext } from 'src/components/BundleProduct/BundleSelectionProvider';
import { BundleProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { ProductSummaryChild } from 'src/components/Basket/utils/basket-customisations';
import { BundleOptionsContext } from 'src/components/BundleProduct/BundleOptionsProvider';
import {
    organizeArtworkPriceInfo,
    sumArtworkPriceAndFee,
} from 'src/components/BundleProduct/ProductBundleSummary/helpers';
import { AtbModalSkeleton } from 'src/elements/Skeletons/Skeletons';

import { AtbDetails, AtbModal } from '../AtbModal/AtbModal';

type Props = {
    product: BundleProduct;
    successModal?: (close: () => void) => React.ReactNode;
    reMountForm();
};

const EMPTY_ARR = [];

const selector = (s: AppendState<AppendCartState>) => {
    const itemIds = s.bundle?.itemIds || EMPTY_ARR;
    return {
        messages: s.bundle?.messages || EMPTY_ARR,
        status: s.bundle?.status || DataStatus.Idle,
        cartItems: s.cart.items.filter((item) => itemIds.indexOf(item.id) > -1),
    };
};

export function BundleAtbModal(props: PropsWithChildren<Props>) {
    const { product, reMountForm } = props;
    const dispatch = useDispatch();
    const state = useSelector(selector, shallowEqual);
    const close = useCallback(() => {
        dispatch(BundleMsg('Bundle.Reset'));
        scrollTo(0, 0);
        reMountForm();
    }, [dispatch, reMountForm]);
    if (state.status === DataStatus.Success) {
        if (props.successModal) {
            return props.successModal(close) as any;
        }
        return <BundleSuccessModal close={close} product={product} />;
    }
    if (state.status === DataStatus.Pending) {
        return <AtbModalSkeleton close={close} />;
    }
    if (state.status === DataStatus.Error) {
        return <MessagesModal messages={state.messages} title="There was a problem with that request" close={close} />;
    }
    return null;
}

const BundleSuccessModal: React.FC<{ close(); product: BundleProduct }> = (props) => {
    const { product } = props;
    const { formData$, maxQty } = useContext(BundleSelectionContext);
    const { bundlePrice: price, artworkPriceBreak } = useContext(BundleOptionsContext);

    const items: any = formData$.value.items.map((item) =>
        item.sizes.map((size) => ({ qty: size.qty, labels: [item.swatch.title, size.size.title] })),
    );
    const itemsFlatten: ProductSummaryChild[] = items.flat();

    // Get price + additional cost (artwork fee and cost) of 2nd logo (or higher)
    const { artworkCost } = organizeArtworkPriceInfo({
        artworkPriceBreak,
        artworksData: formData$.value.artworksData,
        maxQty,
    });
    const { artworksPrice, artworksFee } = sumArtworkPriceAndFee(artworkCost);

    const additionalCost = artworksPrice * maxQty + artworksFee;
    const finalPrice = useMemo(() => {
        return {
            regular_price_incl: 0,
            regular_price: 0,
            final_price_incl: 0,
            final_price: 0,
            price_incl: price.price_incl + additionalCost,
            price: price.price + additionalCost,
            price_highest: price.price_highest,
            price_highest_incl: price.price_highest_incl,
        };
    }, [additionalCost, price.price, price.price_highest, price.price_highest_incl, price.price_incl]);
    const detail: AtbDetails = useMemo(() => {
        return {
            totalPrice: finalPrice,
            itemPrice: -1,
            sku: product.sku,
            image: product.small_image,
            qty: maxQty,
            name: product.name,
            items: itemsFlatten,
        };
    }, [itemsFlatten, finalPrice, maxQty, product.name, product.sku, product.small_image]);

    return (
        <AtbModal
            close={props.close}
            itemIds={[]}
            detail={detail}
            showItemPrice={false}
            showArtworkLink={false}
            noQuantity
        />
    );
};
